// Pure helpers ported 1:1 from the design's logic.jsx, against the API view.
// Copy comes from locales/en/publishing.json through the `t` passed in.
import type { TFunction } from 'i18next';
import type { PublishingTrack, PublishingView, PublishingWriter } from 'types/publishing';

export interface Buckets {
  missing: PublishingTrack[];
  ready: PublishingTrack[];
  blocked: PublishingTrack[];
  /** Hidden ("not mine") or already sent in a batch; listed tracks stay in `ready`. */
  done: PublishingTrack[];
  hiddenCount: number;
}

export function buckets(tracks: PublishingTrack[]): Buckets {
  return {
    missing: tracks.filter((t) => t.state === 'not_listed' && !t.notMine && !t.batchId),
    ready: tracks.filter((t) => t.state === 'listed'),
    blocked: tracks.filter((t) => t.state === 'cant_check' && !t.notMine && !t.batchId),
    done: tracks.filter((t) => t.state !== 'listed' && (t.notMine || !!t.batchId)),
    hiddenCount: tracks.filter((t) => t.notMine).length,
  };
}

export type TileKind = 'alert' | 'cta' | 'ok' | 'warn';

export function tileKind(view: Pick<PublishingView, 'artist'>, b: Buckets): TileKind {
  if (view.artist.identity !== 'usable') return 'alert';
  if (!b.missing.length && b.ready.length) return 'ok';
  if (!b.missing.length) return 'warn';
  return 'cta';
}

/** Tracks the artist has not unchecked, among the eligible ones. */
export function selectedIsrcs(missing: PublishingTrack[], deselected: Set<string>): string[] {
  return missing.map((t) => t.isrc as string).filter((isrc) => !deselected.has(isrc));
}

/** Design `isYou`: the writer name is the artist's legal name (exact, or first + last name). */
export function isYou(legalName: string | null, name: string | null | undefined): boolean {
  const you = (legalName || '').trim().toLowerCase();
  const low = (name || '').toLowerCase();
  if (!you || !low) return false;
  const p = you.split(' ');
  return low === you || (p.length >= 2 && low.includes(p[0]) && low.includes(p[p.length - 1]));
}

export function writerLine(
  t: TFunction,
  legalName: string | null,
  writers?: PublishingWriter[]
): string {
  if (!writers || !writers.length) return '';
  const youLabel = t('writers_you');
  const names = writers.map((w) =>
    w.isArtist || isYou(legalName, w.name) ? youLabel : writerName(w.name)
  );
  const uniq: string[] = [];
  names.forEach((n) => {
    if (!uniq.includes(n)) uniq.push(n);
  });
  if (uniq.length === 1 && uniq[0] === youLabel) return t('writers_you_wrote_this');
  return t('writers_line', { names: uniq.join(', ') });
}

export function writerName(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/(^|[\s\-'])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Album name worth showing: hidden for a single whose album is just the track title. */
export function albumLabel(t: PublishingTrack): string | null {
  if (!t.album) return null;
  const isSingle = (t.kind || '').toLowerCase() === 'single';
  return isSingle && t.album.trim().toLowerCase() === t.title.trim().toLowerCase() ? null : t.album;
}

export function metaBits(t: PublishingTrack): string {
  return [t.kind, albumLabel(t), formatDate(t.releaseDate)].filter(Boolean).join(' · ');
}

export function oldestYear(tracks: PublishingTrack[]): number | null {
  const years = tracks
    .map((t) => (t.releaseDate || '').slice(0, 4))
    .filter((y) => y.length === 4)
    .map(Number)
    .sort((a, b) => a - b);
  return years.length ? years[0] : null;
}

/** Songwriter ID (IPI): optional, 9 to 11 digits once spaces, dots and dashes are stripped. */
export function ipiCheck(value: string): { valid: boolean; digits: string } {
  const digits = (value || '').replace(/[\s.-]/g, '');
  return { valid: !digits || /^\d{9,11}$/.test(digits), digits };
}
